import { AppObject } from '../types'
import { bodyMetrics, findBodyObject, logoUvBoundsInBody } from './patternUV'

/**
 * Pattern template (dieline) generator
 * ------------------------------------
 * Produces a correctly-proportioned PNG the designer authors their wrap artwork
 * against. It marks the seam (u=0/1, the back), the front-center line (u=0.5),
 * and the box where `logo_main` lands in the pattern UV space. Because the
 * pattern is baked with the same UV math (see patternUV.ts) and anchored so the
 * logo sits at u=0.5, anything drawn inside the box lands exactly on the model's
 * logo — the artwork logo and the model logo overlap.
 *
 * The canvas is the texture image itself (flipY:false), so template pixel
 * (col,row) maps to texcoord (col/W, row/H) — the same mapping compositing uses.
 */

const MAX_TEMPLATE_DIM = 2048

/** Find the logo reference part: prefer an exact `logo_main`, else any id containing "logo". */
export function findLogoObject(objects: AppObject[]): AppObject | null {
  return objects.find((o) => o.id.toLowerCase() === 'logo_main') ?? objects.find((o) => /logo/i.test(o.id)) ?? null
}

/** Shared UV→pixel mapping (flipY:false: texcoord maps straight to the canvas grid). */
export function uvToPixel(u: number, v: number, width: number, height: number): [number, number] {
  return [u * width, v * height]
}

export interface PatternTemplate {
  dataUrl: string
  width: number
  height: number
  hasLogo: boolean
}

/**
 * Build a dieline PNG for the current model. Aspect ratio matches
 * circumference:height so artwork isn't distorted when wrapped.
 */
export function generatePatternTemplate(objects: AppObject[]): PatternTemplate | null {
  if (objects.length === 0) return null

  const body = findBodyObject(objects)
  if (!body) return null

  const { circumference, height } = bodyMetrics(body)
  const aspect = circumference / height // width : height of the unrolled surface

  let width: number
  let canvasHeight: number
  if (aspect >= 1) {
    width = MAX_TEMPLATE_DIM
    canvasHeight = Math.max(1, Math.round(MAX_TEMPLATE_DIM / aspect))
  } else {
    canvasHeight = MAX_TEMPLATE_DIM
    width = Math.max(1, Math.round(MAX_TEMPLATE_DIM * aspect))
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = canvasHeight
  const ctx = canvas.getContext('2d')!

  // Background
  ctx.fillStyle = '#f5f5f5'
  ctx.fillRect(0, 0, width, canvasHeight)

  // Faint grid (10 divisions each axis)
  ctx.strokeStyle = 'rgba(0,0,0,0.08)'
  ctx.lineWidth = 1
  for (let i = 1; i < 10; i++) {
    const x = (i / 10) * width
    const y = (i / 10) * canvasHeight
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, canvasHeight)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    ctx.stroke()
  }

  // Seam markers at both edges (u=0 and u=1 meet on the model — this is the back)
  ctx.strokeStyle = '#d92662'
  ctx.setLineDash([12, 8])
  ctx.lineWidth = 3
  ;[1.5, width - 1.5].forEach((x) => {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, canvasHeight)
    ctx.stroke()
  })
  ctx.setLineDash([])
  ctx.fillStyle = '#d92662'
  ctx.font = 'bold 20px Arial'
  ctx.textBaseline = 'top'
  ctx.fillText('SEAM (back)', 8, 8)
  ctx.textAlign = 'right'
  ctx.fillText('SEAM (back)', width - 8, 8)
  ctx.textAlign = 'left'

  // Front-center line at u = 0.5 (aligned to logo_main)
  const cx = width / 2
  ctx.strokeStyle = '#2e7d32'
  ctx.setLineDash([6, 6])
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(cx, 0)
  ctx.lineTo(cx, canvasHeight)
  ctx.stroke()
  ctx.setLineDash([])

  // Logo box: where logo_main lands in the body's UV space
  const logo = findLogoObject(objects)
  const logoUv = logoUvBoundsInBody(body, logo)
  if (logoUv) {
    const [x0, y0] = uvToPixel(logoUv.minU, logoUv.minV, width, canvasHeight)
    const [x1, y1] = uvToPixel(logoUv.maxU, logoUv.maxV, width, canvasHeight)
    const rx = Math.min(x0, x1)
    const ry = Math.min(y0, y1)
    const rw = Math.abs(x1 - x0)
    const rh = Math.abs(y1 - y0)

    ctx.strokeStyle = '#2e7d32'
    ctx.lineWidth = 4
    ctx.strokeRect(rx, ry, rw, rh)
    ctx.fillStyle = 'rgba(46,125,50,0.12)'
    ctx.fillRect(rx, ry, rw, rh)
    ctx.fillStyle = '#2e7d32'
    ctx.font = 'bold 22px Arial'
    ctx.fillText('LOGO — align here', rx + 6, Math.max(2, ry - 26))
  } else {
    ctx.fillStyle = '#2e7d32'
    ctx.font = 'bold 20px Arial'
    ctx.fillText('FRONT (u=0.5, no logo_main found)', cx + 8, 8)
  }

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width,
    height: canvasHeight,
    hasLogo: !!logoUv,
  }
}

/** Trigger a browser download of a data URL. */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  link.click()
}
